import React from 'react';
import { isRecoverableChunkLoadError, recoverFromChunkLoadError } from '../utils/runtimeRecovery';

interface ErrorBoundaryState {
  hasError: boolean;
  message?: string;
}

class ErrorBoundary extends React.Component<React.PropsWithChildren, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, message: error.message };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('Unhandled UI error', { error, errorInfo });

    if (isRecoverableChunkLoadError(error)) {
      recoverFromChunkLoadError().catch((recoveryError) => {
        console.warn('Automatic chunk recovery failed', recoveryError);
      });
    }
  }

  handleRefresh = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-100 dark:bg-slate-950 flex items-center justify-center p-6">
          <div className="w-full max-w-xl rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xl p-6">
            <h1 className="text-xl font-bold text-slate-900 dark:text-white">Something went wrong</h1>
            <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
              The app encountered an unexpected error. Refresh to recover.
            </p>
            {this.state.message && (
              <p className="mt-3 rounded-lg bg-slate-100 dark:bg-slate-800 p-3 text-xs text-slate-500 dark:text-slate-300 font-mono break-words">
                {this.state.message}
              </p>
            )}
            <button
              onClick={this.handleRefresh}
              className="mt-5 inline-flex items-center rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700"
            >
              Reload application
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
